import {useContext, useEffect, useRef, useState} from 'react';
import {create} from '@bufbuild/protobuf';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {FULL_SWING_SESSION_ID_STORAGE_KEY} from '../services/fullSwingAuthSessionStorage';
import {useConnectClients} from '../grpc/ConnectClientContext';
import {
  PartnerKioskUpdate_UserSessionUpdateData, PartnerKioskUpdatesStreamRequestSchema, PartnerKioskUpdate_UpdateKind
} from '../../generated/partner_service_pb';
import {StoreContext} from '../components/StoreContextProvider';

// useStreamKioskUpdates — the legacy live-session consumer (useLiveSessionStream
// dispatches here when sessionBackend === 'legacy').
//
// Subscribes to the partner-service kiosk-update stream and surfaces its
// UserSessionUpdateData frames. The legacy stream emits the canonical shape
// natively, so this is a passthrough — no projection. Gated on the smg-core
// `session` token (mirroring useStreamPlayerState's bayPlayerJwt gate), which it
// passes in the request body (legacy auth), not as a Bearer header. The
// session_id is read from AsyncStorage at open time; there is no sequence cursor.

type Props = { enabled: boolean };

export type KioskUpdatesStream = {
  // Latest UserSessionUpdateData frame from the legacy kiosk stream, or
  // undefined before the first frame arrives. Emitted as-is (passthrough).
  update: PartnerKioskUpdate_UserSessionUpdateData | undefined;
  legacyConfirmationEvent: LegacyConfirmationEvent | undefined;
  isConnected: boolean;
  streamError: string | null;
};

export type LegacyConfirmationEvent =
  | {
  kind: 'requested', sessionId: string, timestamp: number, authSessionToken: string,
  subjectUserId: string
}
  | {
  kind: 'removed', sessionId: string, userId: number, timestamp: number, authSessionToken: string, subjectUserId: string
};

const useStreamKioskUpdates = ({enabled}: Props): KioskUpdatesStream => {
  const {partner} = useConnectClients();
  const {session} = useContext(StoreContext);
  const [update, setUpdate] = useState<PartnerKioskUpdate_UserSessionUpdateData | undefined>(undefined);
  const [confirmationEvent, setConfirmationEvent] = useState<LegacyConfirmationEvent | undefined>(undefined);
  const [isConnected, setIsConnected] = useState(false);
  const [streamError, setError] = useState<string | null>(null);
  const streamGenerationRef = useRef(0);

  useEffect(() => {
    const generation = streamGenerationRef.current + 1;
    streamGenerationRef.current = generation;
    const authSession = session;
    let disposed = false;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;
    const isCurrent = () => !disposed && streamGenerationRef.current === generation;

    // A frame belongs to the exact auth session that opened this stream.
    // Clear all snapshots before opening/reopening so an event captured for a
    // previous kiosk user cannot survive the token boundary for one render.
    setUpdate(undefined);
    setConfirmationEvent(undefined);
    setIsConnected(false);
    setError(null);

    const clearReconnect = () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
    };

    const cleanup = () => {
      clearReconnect();
      controller?.abort();
      controller = null;
    };

    const scheduleReconnect = () => {
      if (!enabled || !isCurrent()) return;
      clearReconnect();
      reconnectTimeout = setTimeout(() => {
        if (!isCurrent()) return;
        start();
      }, 5000);
    };

    const loadSessionId = async (): Promise<string> => {
      try {
        return await AsyncStorage.getItem(FULL_SWING_SESSION_ID_STORAGE_KEY) ?? '';
      } catch (error) {
        console.error('[useStreamKioskUpdates] failed to load session_id', error);
        return '';
      }
    };

    const start = async () => {
      if (!isCurrent()) return;
      // The stream needs both a session token and a session_id; opening without
      // either is rejected and would spin the reconnect loop. Check both first.
      if (!authSession?.token) {
        console.log('[useStreamKioskUpdates] waiting for session.token before opening stream');
        scheduleReconnect();
        return;
      }

      const sessionId = await loadSessionId();
      if (!isCurrent()) return;
      if (!sessionId) {
        console.log('[useStreamKioskUpdates] waiting for session_id before opening stream');
        scheduleReconnect();
        return;
      }

      controller?.abort();
      controller = new AbortController();
      const activeController = controller;
      console.log(`[useStreamKioskUpdates] Starting stream (sessionId=${sessionId})`);
      setConfirmationEvent(undefined);

      try {
        const request = create(PartnerKioskUpdatesStreamRequestSchema, {token: authSession.token, sessionId});

        // Legacy auth is via request body (token field) — no Authorization
        // header, in contrast to the bay-session stream which uses Bearer.
        const iterable = partner.partnerKioskUpdatesStream(request, {signal: activeController.signal});

        for await (const reply of iterable) {
          if (activeController.signal.aborted || !isCurrent()) break;

          // Heartbeat frames carry no user data. Surface as a connectivity
          // tick (isConnected=true) without updating `update` so consumers
          // do not re-fire on heartbeats.
          if (reply.update === PartnerKioskUpdate_UpdateKind.HEART_BEAT) {
            if (isCurrent()) {
              setIsConnected(true);
              setError(null);
            }
            continue;
          }

          if (reply.data.case === 'userSessionUpdateData') {
            if (isCurrent() && reply.data.value.sessionId === sessionId) {
              setUpdate(reply.data.value);
              setIsConnected(true);
              setError(null);
            }
          } else if (reply.data.case === 'userSessionParticipationConfirmationData') {
            if (isCurrent() && reply.data.value.sessionId === sessionId) {
              setConfirmationEvent({
                kind: 'requested', sessionId: reply.data.value.sessionId, timestamp: Number(reply.timestamp),
                authSessionToken: authSession.token, subjectUserId: String(authSession.userId)
              });
              setIsConnected(true);
              setError(null);
            }
          } else if (reply.data.case === 'userSessionPendingConfirmationRemovalData') {
            if (isCurrent() && reply.data.value.sessionId === sessionId) {
              setConfirmationEvent({
                kind: 'removed', sessionId: reply.data.value.sessionId, userId: reply.data.value.userId,
                timestamp: Number(reply.timestamp), authSessionToken: authSession.token,
                subjectUserId: String(authSession.userId)
              });
              setIsConnected(true);
              setError(null);
            }
          }
        }

        if (!activeController.signal.aborted && isCurrent()) {
          setIsConnected(false);
          scheduleReconnect();
        }

      } catch (e: any) {
        if (activeController.signal.aborted || !isCurrent()) return;

        setIsConnected(false);
        setError(e?.message || 'Stream error');
        scheduleReconnect();
      }
    };

    if (enabled) {
      start();

    } else {
      cleanup();
    }

    return () => {
      disposed = true;
      cleanup();
    };
    // Restart when authentication changes. The session id belongs to the same
    // persisted legacy login transaction and is loaded before each reconnect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, session?.token]);

  return {update, legacyConfirmationEvent: confirmationEvent, isConnected, streamError};
};

export default useStreamKioskUpdates;
